import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon, UIIconProps} from '@dropbox/dig-icons';
import {ElementType, ReactNode, Ref} from 'react';

import styles from './Dashboard.module.css';
import {TileId, TileSpan} from './dashboardModel';
import {useDashboardTileId} from './DashboardTileContext';
import {useTileImpressionRef} from './HomeVisitContext';
import {getTileLabel, getTileMeta} from './tileCatalog';
import {useTileEditEmbedded, useTileSpan} from './TileSizeContext';

const COL_SPAN_CLASS: Record<number, string> = {
  1: styles.colSpan1,
  2: styles.colSpan2,
};

const ROW_SPAN_CLASS: Record<number, string> = {
  1: styles.rowSpan1,
  2: styles.rowSpan2,
  3: styles.rowSpan3,
  4: styles.rowSpan4,
};

export const tileSpanClassNames = (span: TileSpan): string =>
  [COL_SPAN_CLASS[span.cols] ?? styles.colSpan1, ROW_SPAN_CLASS[span.rows] ?? styles.rowSpan1].join(
    ' '
  );

export const Tile = ({
  tileId,
  label,
  icon,
  onClick,
  children,
  accessory,
  floatAccessory,
  labelAccessory,
  labelControl,
  labelTagName,
  className,
  bare,
  interactive,
  bodyRef,
}: {
  tileId?: TileId;
  /** Pass `null` / `false` to hide the default label or icon. */
  label?: ReactNode | null;
  icon?: UIIconProps['src'] | false;
  onClick?: () => void;
  children: ReactNode;
  accessory?: ReactNode;
  /** Zero-height wrapper for tall header controls (e.g. SegmentedControl). */
  floatAccessory?: boolean;
  labelAccessory?: ReactNode;
  /** Always-visible control beside the label (labelAccessory only shows on hover). */
  labelControl?: ReactNode;
  /** Render the label as this element, e.g. 'h2' where the tile is a page section. */
  labelTagName?: ElementType;
  className?: string;
  bare?: boolean;
  /** Shade on hover/focus when click handling is on an outer link. */
  interactive?: boolean;
  /** On the body under the header, e.g. to fade it in after a skeleton (useSkeletonFadeIn). */
  bodyRef?: Ref<HTMLDivElement>;
}) => {
  const meta = tileId ? getTileMeta(tileId) : undefined;
  const effectiveLabel =
    label === null ? undefined : (label ?? (tileId ? getTileLabel(tileId) : undefined));
  const effectiveIcon = icon === false ? undefined : (icon ?? meta?.icon);
  const effectiveSpan = useTileSpan();
  const embeddedInEditChrome: boolean = useTileEditEmbedded();
  const isInteractive = Boolean(onClick || interactive) && !embeddedInEditChrome;
  const shade = isInteractive ? withShade({}) : null;
  const impressionRef = useTileImpressionRef(useDashboardTileId() ?? tileId);

  return (
    <Box
      ref={impressionRef}
      className={[
        tileSpanClassNames(effectiveSpan),
        styles.tileShell,
        embeddedInEditChrome ? styles.tileEditEmbedded : '',
        isInteractive ? atoms({cursor: 'pointer'}) : '',
        shade?.className ?? '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={shade?.style}
      height="100%"
      padding="16"
      borderRadius={embeddedInEditChrome || bare ? undefined : 'Medium'}
      borderStyle={embeddedInEditChrome || bare ? undefined : 'Solid'}
      borderColor={embeddedInEditChrome || bare ? undefined : 'Border Subtle'}
      backgroundColor={bare ? undefined : 'Background Base'}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Stack needs display/height props so the scrollable body gets a bounded flex height. */}
      <Stack gap="12" display="flex" height="100%">
        {(effectiveLabel || accessory) && (
          <Split alignY="center" gap="8">
            <Split.Item width="fill">
              {effectiveLabel && (
                <Box display="flex" alignItems="center" style={{gap: 6, minWidth: 0}}>
                  {effectiveIcon && (
                    <UIIcon src={effectiveIcon} className={atoms({color: 'Text Subtle'})} />
                  )}
                  <Text
                    size="large"
                    isBold
                    tagName={labelTagName}
                    className={labelTagName ? atoms({margin: '0'}) : undefined}
                  >
                    {effectiveLabel}
                  </Text>
                  {labelControl ? (
                    <Box display="flex" alignItems="center" marginLeft="8">
                      {labelControl}
                    </Box>
                  ) : null}
                  {labelAccessory ? (
                    <Box className={styles.tileAccessory} display="flex" alignItems="center">
                      {labelAccessory}
                    </Box>
                  ) : null}
                </Box>
              )}
            </Split.Item>
            {accessory && (
              <Split.Item>
                <Box
                  className={styles.tileAccessory}
                  display="flex"
                  alignItems="center"
                  style={floatAccessory ? {height: 0} : undefined}
                >
                  {accessory}
                </Box>
              </Split.Item>
            )}
          </Split>
        )}
        {/* Horizontal slack matches row hover shades (marginInline: -8). */}
        <Box
          ref={bodyRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            marginInline: -8,
            paddingInline: 8,
          }}
        >
          {children}
        </Box>
      </Stack>
    </Box>
  );
};
